import { makeAutoObservable, observable, runInAction } from "mobx";
import { isCancelledError } from "@tanstack/react-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import {
  projectMemberMeKey,
  projectMemberMeQuery,
  projectMembersQuery,
} from "@/queries/project-resources";
import { scopedKey } from "@/queries/scope";
import type { RootStore } from "./root.store";
import { queryCacheScope } from "./query-cache-scope";
import type {
  TProjectBulkInviteFormData,
  TProjectInvitation,
  TProjectMembership,
} from "@/types/project";

export interface IProjectMemberFilters {
  search: string;
  role: "all" | "admin" | "member" | "guest";
}

export class ProjectMembersStore {
  membersByProject = observable.map<string, TProjectMembership[]>();
  invitationsByProject = observable.map<string, TProjectInvitation[]>();
  filters: IProjectMemberFilters = {
    search: "",
    role: "all",
  };
  isLoading = false;
  isInvitationsLoading = false;
  isInviting = false;
  activeMutationMemberId: string | null = null;
  activeInvitationId: string | null = null;

  private rootStore: RootStore;
  /**
   * Per-project count of confirmed membership writes. A cached read samples it
   * and, if a write removed the entry it was filling (which cancels it), reads
   * again after the write rather than failing. Not observable: bookkeeping
   * about requests, as `ProjectStore.detailWriteSeq` is.
   */
  private membershipWriteSeq = new Map<string, number>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false, membershipWriteSeq: false } as never, {
      autoBind: true,
    });
  }

  get currentProjectId(): string | null {
    return this.rootStore.router.projectId ?? null;
  }

  get currentProjectMembers(): TProjectMembership[] {
    const pid = this.currentProjectId;
    if (!pid) return [];
    return this.membersByProject.get(pid) ?? [];
  }

  get currentProjectInvitations(): TProjectInvitation[] {
    const pid = this.currentProjectId;
    if (!pid) return [];
    return this.invitationsByProject.get(pid) ?? [];
  }

  get filteredMembers(): TProjectMembership[] {
    const search = this.filters.search.trim().toLowerCase();

    return this.currentProjectMembers.filter((member) => {
      const detail = member.member_detail;
      const matchesSearch =
        search.length === 0 ||
        detail?.display_name.toLowerCase().includes(search) ||
        detail?.email?.toLowerCase().includes(search);

      if (!matchesSearch) return false;

      if (this.filters.role === "all") return true;
      if (this.filters.role === "admin") return member.role === "project.lead";
      if (this.filters.role === "member") return member.role === "project.member";
      return false;
    });
  }

  setFilter<K extends keyof IProjectMemberFilters>(key: K, value: IProjectMemberFilters[K]): void {
    this.filters[key] = value;
  }

  /**
   * The project's members (R9), for this store and for `IssueMetadataStore`,
   * which used to read the same endpoint into a second cache. With the
   * `query-cache` flag off this is the service GET. With it on, it goes
   * through the access-sensitive query: reads that overlap share one request,
   * and once it settles nothing is served from it again (contract §2).
   */
  readMembers(slug: string, projectId: string): Promise<TProjectMembership[]> {
    const service = this.rootStore.projectMemberService;
    const scope = queryCacheScope(this.rootStore, slug, "ProjectMembersStore");
    if (!scope) return service.listMembers(slug, projectId);
    return this.readMembership(projectId, () =>
      getQueryClient().fetchQuery(projectMembersQuery(service, scope, projectId)),
    );
  }

  /** The signed-in user's own membership of the project (R10), read the way
   *  `readMembers` reads the roster. */
  readMemberMe(slug: string, projectId: string): Promise<TProjectMembership> {
    const service = this.rootStore.projectMemberService;
    const scope = queryCacheScope(this.rootStore, slug, "ProjectMembersStore");
    if (!scope) return service.memberMe(slug, projectId);
    return this.readMembership(projectId, () =>
      getQueryClient().fetchQuery(projectMemberMeQuery(service, scope, projectId)),
    );
  }

  /** A cached membership read that a write overtook is read again, after it. */
  private async readMembership<T>(projectId: string, read: () => Promise<T>): Promise<T> {
    const seqAtRequest = this.membershipWriteSeq.get(projectId) ?? 0;
    try {
      return await read();
    } catch (error) {
      if (
        isCancelledError(error) &&
        (this.membershipWriteSeq.get(projectId) ?? 0) !== seqAtRequest
      ) {
        return this.readMembership(projectId, read);
      }
      throw error;
    }
  }

  /**
   * Record a confirmed membership write. With the `query-cache` flag on, also
   * drop the project's cached roster (R9, every search) and the user's own
   * membership (R10), cancelling a read of either still in flight: it was
   * asked before the write, and a later reader must not join it (contract
   * §3.1). The detail (R1) is the project store's to drop.
   */
  private markMembershipWrite(slug: string, projectId: string): void {
    this.membershipWriteSeq.set(projectId, (this.membershipWriteSeq.get(projectId) ?? 0) + 1);
    const scope = queryCacheScope(this.rootStore, slug, "ProjectMembersStore");
    const client = peekQueryClient();
    if (!scope || !client) return;
    client.removeQueries({ queryKey: scopedKey(scope, "project", projectId, "members") });
    client.removeQueries({ queryKey: projectMemberMeKey(scope, projectId), exact: true });
  }

  async fetchMembers(slug: string, projectId: string): Promise<TProjectMembership[]> {
    this.isLoading = true;
    try {
      const members = await this.readMembers(slug, projectId);
      runInAction(() => {
        this.membersByProject.set(projectId, members);
      });
      return members;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchInvitations(slug: string, projectId: string): Promise<TProjectInvitation[]> {
    this.isInvitationsLoading = true;
    try {
      const invitations = await this.rootStore.projectInvitationService.list(slug, projectId);
      runInAction(() => {
        this.invitationsByProject.set(projectId, invitations);
      });
      return invitations;
    } finally {
      runInAction(() => {
        this.isInvitationsLoading = false;
      });
    }
  }

  async inviteMembers(
    slug: string,
    projectId: string,
    data: TProjectBulkInviteFormData,
    source: AnalyticsSource = "page",
  ): Promise<TProjectInvitation[]> {
    this.isInviting = true;
    try {
      const created = await this.rootStore.projectInvitationService.invite(slug, projectId, data);
      // Before the refetch: the invites exist even if the list reload fails.
      track("project_member_invited", { project_id: projectId, count: created.length, source });
      await this.fetchInvitations(slug, projectId);
      return created;
    } finally {
      runInAction(() => {
        this.isInviting = false;
      });
    }
  }

  async addExistingMembers(
    slug: string,
    projectId: string,
    members: { member_id: string; role: string }[],
    source: AnalyticsSource = "page",
  ): Promise<TProjectMembership[]> {
    this.isInviting = true;
    try {
      const created = await this.rootStore.projectMemberService.addMembers(slug, projectId, {
        members,
      });
      // The detail carries the leads and `is_member`, which the add changed.
      this.rootStore.project.markDetailChanged(projectId);
      // Before the refetch: the members are added even if the list reload fails.
      track("project_member_added", { project_id: projectId, count: created.length, source });
      this.markMembershipWrite(slug, projectId);
      await this.fetchMembers(slug, projectId);
      return created;
    } finally {
      runInAction(() => {
        this.isInviting = false;
      });
    }
  }

  async updateMemberRole(
    slug: string,
    projectId: string,
    memberId: string,
    role: string,
    source: AnalyticsSource = "page",
  ): Promise<TProjectMembership> {
    this.activeMutationMemberId = memberId;
    try {
      const updated = await this.rootStore.projectMemberService.updateMember(
        slug,
        projectId,
        memberId,
        { role },
      );
      this.markMembershipWrite(slug, projectId);
      runInAction(() => {
        const list = this.membersByProject.get(projectId) ?? [];
        this.membersByProject.set(
          projectId,
          list.map((m) => (m.id === memberId ? updated : m)),
        );
        // A role change can make or unmake a lead, which the detail lists.
        this.rootStore.project.markDetailChanged(projectId);
      });
      track("project_member_updated", {
        project_id: projectId,
        member_id: memberId,
        fields: ["role"],
        role,
        source,
      });
      return updated;
    } finally {
      runInAction(() => {
        this.activeMutationMemberId = null;
      });
    }
  }

  async removeMember(
    slug: string,
    projectId: string,
    memberId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeMutationMemberId = memberId;
    try {
      await this.rootStore.projectMemberService.removeMember(slug, projectId, memberId);
      this.markMembershipWrite(slug, projectId);
      runInAction(() => {
        const list = this.membersByProject.get(projectId) ?? [];
        this.membersByProject.set(
          projectId,
          list.filter((m) => m.id !== memberId),
        );
        // The detail lists the leads, and says whether the viewer is a member.
        this.rootStore.project.markDetailChanged(projectId);
      });
      track("project_member_removed", { project_id: projectId, member_id: memberId, source });
    } finally {
      runInAction(() => {
        this.activeMutationMemberId = null;
      });
    }
  }

  async revokeInvitation(
    slug: string,
    projectId: string,
    invitationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      await this.rootStore.projectInvitationService.revoke(slug, projectId, invitationId);
      runInAction(() => {
        const list = this.invitationsByProject.get(projectId) ?? [];
        this.invitationsByProject.set(
          projectId,
          list.filter((inv) => inv.id !== invitationId),
        );
      });
      track("project_invitation_revoked", {
        project_id: projectId,
        invitation_id: invitationId,
        source,
      });
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }
}
